// authActions.js or inside authSlice.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { GlobalService, HttpService } from "../../modules";
import { webSocketService } from "../../services/websocketService";
import i18n from "../../i18n";
import { getCurrentLanguageCode } from "../../utils/languageHeaders";
import {
    ApplePayload,
    AuthResponse,
    ConfirmEmailPayload,
    FacebookPayload,
    ForgotType,
    GooglePayload,
    LoginPayload,
    RegisterPayload,
    ResendEmailPayload,
    ResetPayload,
    User,
} from "../../modules/shared/model/AuthInterfaces";
import { RoleEnum } from "../../modules/shared/model/role.enum";
import { StatusEnum } from "../../modules/shared/model/status.enum";
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import { getAuthErrorMessage } from "../../modules/auth/AuthErrorHandling";
import { syncLocalFavorites } from "../Slices/favoritesSlice";
import { createUserNotificationAction } from "./UserNotificationActions";
import { NotificationTypeEnum, NotificationStatusEnum } from "../../modules/shared/model/user-notification.enum";
import { createUserNotifications, getUserNotifications, createUserPrivacies, getUserPrivacies } from "./ClientActions";
import { LogoutManually } from "../Slices/authSlice";

// Helper function to create promotional notification for users without phone
const createPromotionalNotification = async (userId: string) => {
    try {
        await createUserNotificationAction({
            user: { id: userId },
            type: { id: NotificationTypeEnum["Banner"] },
            status: { id: NotificationStatusEnum.Unread },
            title: i18n.t("notifications.promotional.freeQuotesTitle"),
            message: i18n.t("notifications.promotional.freeQuotesMessage"),
            url: "/professionals"
        });
        console.log("✅ Promotional notification created successfully");
    } catch (error) {
        console.error("Failed to create promotional notification:", error);
    }
};

// Helper function to initialize notification settings for users without phone
const initializeNotificationSettings = async (user: User, dispatch: any) => {
    try {
        // Check if user has no phone number
        if (!user.phone) {
            console.log("📱 User has no phone number, checking notification settings...");

            // Check if notification settings already exist
            const existingSettings = await dispatch(getUserNotifications({ id: user.id }));

            // Only create if no record exists (payload is null, undefined, or empty)
            const noRecordExists =
                !existingSettings ||
                !existingSettings.payload ||
                existingSettings.payload === null ||
                existingSettings.payload === "" ||
                (typeof existingSettings.payload === "string" && existingSettings.payload.length === 0);

            if (noRecordExists) {
                console.log("🔔 No notification settings found - creating default settings for user...");


                // Create notification settings with all values set to true
                const notificationData = {
                    userId: { id: user.id },
                    // Common settings for all users
                    pushMarketing: true,
                    pushMessage: true,
                    pushProject: true,
                    emailMarketing: true,
                    emailMessage: true,
                    emailProject: true,
                    pushInvite: true,
                    emailInvite: true,
                    pushProposal: true,
                    emailProposal: true,
                };

                await dispatch(createUserNotifications({ data: notificationData }));
                console.log("✅ Notification settings created successfully");
            } else {
                console.log("ℹ️ Notification settings already exist - skipping creation");
            }
        } else {
            console.log("📞 User has phone number - skipping notification initialization");
        }
    } catch (error) {
        console.error("❌ Failed to initialize notification settings:", error);
        // Silently fail - don't block login flow
    }
};

// Helper function to initialize privacy settings for users without phone
const initializePrivacySettings = async (user: User, dispatch: any) => {
    try {
        // Check if user has no phone number
        if (!user.phone) {
            console.log("🔒 User has no phone number, checking privacy settings...");

            // Check if privacy settings already exist
            const existingPrivacy = await dispatch(getUserPrivacies({ id: user.id }));

            // Only create if no record exists (payload is null, undefined, or empty)
            const noRecordExists =
                !existingPrivacy ||
                !existingPrivacy.payload ||
                existingPrivacy.payload === null ||
                existingPrivacy.payload === "" ||
                (typeof existingPrivacy.payload === "string" && existingPrivacy.payload.length === 0);

            if (noRecordExists) {
                console.log("🔐 No privacy settings found - creating default settings for user...");

                // Create privacy settings with visibility = public (1) and all booleans = true
                const privacyData = {
                    userId: { id: user.id },
                    visibility: { id: 1 }, // 1 = Public
                    isShowAvailability: true,
                    isShowEarnings: true,
                    isShowLocation: true,
                    isShowContact: true,
                    isShowBudget: true,
                };

                await dispatch(createUserPrivacies({ data: privacyData }));
                console.log("✅ Privacy settings created successfully");
            } else {
                console.log("ℹ️ Privacy settings already exist - skipping creation");
            }
        } else {
            console.log("📞 User has phone number - skipping privacy initialization");
        }
    } catch (error) {
        console.error("❌ Failed to initialize privacy settings:", error);
        // Silently fail - don't block login flow
    }
};

// Helper function to fetch and save user currency from general settings
const fetchAndSaveCurrency = async (userId: string, dispatch: any) => {
    try {
        // Show loader while fetching currency
        dispatch(showLoader());

        // Step 1: Try to get currency from general settings
        const result = await dispatch(getUserGeneralSetting({ id: userId, isShowLoader: false }));

        if (result?.payload?.currencyId) {
            const currency = result.payload.currencyId;
            localStorage.setItem("selectedCurrencyCode", currency.initial || currency.name || "");
            localStorage.setItem("selectedCurrency", JSON.stringify(currency));
            dispatch(hideLoader());
            return; // Currency found, no need to detect location
        }

        // Step 2: If no currency in general settings, detect location
        try {
            const locationResponse = await HttpService.post(
                "api/v1/auth/detect-location",
                {} // Empty body - the backend will auto-detect IP
            );

            if (locationResponse?.currencyCode) {
                const detectedCurrency = {
                    initial: locationResponse.currencyCode,
                    name: locationResponse.currencyCode,
                    id: locationResponse.currencyId || ""
                };

                localStorage.setItem("selectedCurrencyCode", detectedCurrency.initial);
                localStorage.setItem("selectedCurrency", JSON.stringify(detectedCurrency));
            }
        } catch (locationError) {
            // Silent fail for location detection
        }

        // Hide loader after location detection
        dispatch(hideLoader());
    } catch (error) {
        // If general settings fetch fails, try location detection as fallback
        try {
            const locationResponse = await HttpService.post(
                "api/v1/auth/detect-location",
                {} // Empty body - the backend will auto-detect IP
            );

            if (locationResponse?.currencyCode) {
                const detectedCurrency = {
                    initial: locationResponse.currencyCode,
                    name: locationResponse.currencyCode,
                    id: locationResponse.currencyId || ""
                };

                localStorage.setItem("selectedCurrencyCode", detectedCurrency.initial);
                localStorage.setItem("selectedCurrency", JSON.stringify(detectedCurrency));
            }
        } catch (locationError) {
            // Silent fail for location detection fallback
        } finally {
            // Always hide loader in fallback scenario
            dispatch(hideLoader());
        }
    }
};

// Single reusable function for post-login navigation
const handlePostLoginNavigation = (user: any) => {
    // Check for redirect URL and redirect if exists
    const redirectUrl = localStorage.getItem("redirectAfterLogin");
    if (redirectUrl) {
        localStorage.removeItem("redirectAfterLogin");
        window.location.href = redirectUrl;
        return;
    }

    // Apply same navigation logic as LoginComponent
    const { status, role, phone, id } = user;

    // If user is inactive, redirect to email confirmation
    if (Number(status?.id) === StatusEnum.inactive) {
        window.location.href = "/auth/confirm-email";
    } else if (Number(status?.id) === StatusEnum.active) {
        // Active user - redirect based on role
        if (Number(role?.id) === RoleEnum.professional || Number(role?.id) === RoleEnum.internationl_professional) {
            // Professional or International Professional
            if (!phone) {
                // Create notification record
                createPromotionalNotification(id);
                window.location.href = "/myprofile";
            } else {
                window.location.href = "/professionals";
            }
        } else if (Number(role?.id) === RoleEnum.client) {
            // Client
            if (!phone) {
                window.location.href = "/client/profile";
            } else {
                window.location.href = "/clients";
            }
        } else if (Number(role?.id) === RoleEnum.admin) {
            // Admin
            window.location.href = "/admin/dashboard";
        }
    }
};

export const registerUser = createAsyncThunk<AuthResponse, RegisterPayload>(
    "auth/registerUser",
    async (formData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            
            const payload = {
                ...formData,
                cameraPermission: true,
                enableNotification: true,
                language: getCurrentLanguageCode(),
            };

            const response = await HttpService.post("api/v1/auth/email/register", payload);
            dispatch(hideLoader());

            if (response?.user) {
                response.user = {
                    ...response.user,
                    cameraPermission: true,
                    enableNotification: true,
                };
            }

            const confirmUserInfo = {
                id: response?.user?.id,
                email: response?.user?.email,
            };
            await GlobalService.setStorage("confirmUserInfo", JSON.stringify(confirmUserInfo));

            const active_user_id = parseInt(response?.user?.status?.id);

            if (active_user_id == 2) {
                GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.verifyEmail"));
            } else {
                await GlobalService.setStorage("refreshToken", response.refreshToken);
                await GlobalService.setStorage("token", response.token);
                await GlobalService.setStorage("user", response.user);

                // Initialize notification settings if user has no phone
                await initializeNotificationSettings(response.user, dispatch);

                // Initialize privacy settings if user has no phone
                await initializePrivacySettings(response.user, dispatch);

                GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.registerSuccess"));

                // Sync local favorites with backend
                await dispatch(syncLocalFavorites());

                // Check for redirect URL and redirect if exists
                const redirectUrl = localStorage.getItem("redirectAfterLogin");
                if (redirectUrl) {
                    localStorage.removeItem("redirectAfterLogin");
                    window.location.href = redirectUrl;
                }
            }

            return response; // now returns full AuthResponse
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorKey = errorData?.email || errorData?.password || "User Registered failed";

            const message = getAuthErrorMessage(errorKey, "User Registered failed");
            GlobalService.showToast("error", i18n.t("toast.auth.registerFailed"), message);
            return rejectWithValue(errorKey);
        }
    }
);

export const loginUser = createAsyncThunk<AuthResponse, LoginPayload>(
    "auth/loginUser",
    async (formData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response: AuthResponse = await HttpService.post("api/v1/auth/email/login", formData);

            dispatch(hideLoader());

            const active_user_id = parseInt(response?.user?.status?.id);
            const confirmUserInfo = {
                id: response?.user?.id,
                email: response?.user?.email,
            };
            await GlobalService.setStorage("confirmUserInfo", JSON.stringify(confirmUserInfo));

            if (active_user_id == 2) {
                GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.verifyEmail"));
            } else {
                await GlobalService.setStorage("token", response.token);
                await GlobalService.setStorage("refreshToken", response.refreshToken);
                await GlobalService.setStorage("user", response.user);

                // Fetch general settings and save currency to localStorage
                await fetchAndSaveCurrency(response.user.id, dispatch);

                // Initialize notification settings if user has no phone
                await initializeNotificationSettings(response.user, dispatch);

                // Initialize privacy settings if user has no phone
                await initializePrivacySettings(response.user, dispatch);

                GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.loginSuccess"));

                // Sync local favorites with backend
                await dispatch(syncLocalFavorites());

                // Check for redirect URL and redirect if exists
                const redirectUrl = localStorage.getItem("redirectAfterLogin");
                if (redirectUrl) {
                    localStorage.removeItem("redirectAfterLogin");
                    window.location.href = redirectUrl;
                }
            }

            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorKey = errorData?.email || errorData?.password || "User login failed";

            const message = getAuthErrorMessage(errorKey, "User login failed");
            GlobalService.showToast("error", i18n.t("toast.auth.loginFailed"), message);
            return rejectWithValue(errorKey);
        }
    }
);
export const googleLogin = createAsyncThunk<AuthResponse, GooglePayload>(
    "auth/googleLogin",
    async (formData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post("api/v1/auth/google/login", formData);
            dispatch(hideLoader());
            await GlobalService.setStorage("token", response.token);
            await GlobalService.setStorage("user", response.user);

            // Fetch general settings and save currency to localStorage
            await fetchAndSaveCurrency(response.user.id, dispatch);

            // Initialize notification settings if user has no phone
            await initializeNotificationSettings(response.user, dispatch);

            // Initialize privacy settings if user has no phone
            await initializePrivacySettings(response.user, dispatch);

            // Use the centralized navigation function
            handlePostLoginNavigation(response.user);

            return response; // now returns full AuthResponse
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.email || errorData?.google || "Google login failed";

            GlobalService.showToast("error", i18n.t("toast.auth.googleLoginFailed"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);
export const facebookLogin = createAsyncThunk<AuthResponse, FacebookPayload>(
    "auth/facebookLogin",
    async (formData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post("api/v1/auth/facebook/login", formData);
            dispatch(hideLoader());
            await GlobalService.setStorage("token", response.token);
            await GlobalService.setStorage("user", response.user);

            // Fetch general settings and save currency to localStorage
            await fetchAndSaveCurrency(response.user.id, dispatch);

            // Initialize notification settings if user has no phone
            await initializeNotificationSettings(response.user, dispatch);

            // Initialize privacy settings if user has no phone
            await initializePrivacySettings(response.user, dispatch);

            // Use the centralized navigation function
            handlePostLoginNavigation(response.user);

            return response; // now returns full AuthResponse
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.email || errorData?.facebook || "Facebook login failed";

            GlobalService.showToast("error", i18n.t("toast.auth.facebookLoginFailed"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const appleLogin = createAsyncThunk<AuthResponse, ApplePayload>(
    "auth/appleLogin",
    async (formData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post("api/v1/auth/apple/login", formData);
            dispatch(hideLoader());
            await GlobalService.setStorage("token", response.token);
            await GlobalService.setStorage("user", response.user);

            // Fetch general settings and save currency to localStorage
            await fetchAndSaveCurrency(response.user.id, dispatch);

            // Initialize notification settings if user has no phone
            await initializeNotificationSettings(response.user, dispatch);

            // Initialize privacy settings if user has no phone
            await initializePrivacySettings(response.user, dispatch);

            // Use the centralized navigation function
            handlePostLoginNavigation(response.user);

            return response; // now returns full AuthResponse
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.email || errorData?.facebook || "Apple login failed";

            GlobalService.showToast("error", i18n.t("toast.auth.appleLoginFailed"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);
export const ForgotPassword = createAsyncThunk<void, ForgotType>(
    "auth/ForgotPassword",
    async (formData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const currentLanguage = getCurrentLanguageCode();
            const payload = {
                ...formData,
                language: currentLanguage,
            };

            console.log("[ForgotPassword] Sending request with language:", currentLanguage, "Payload:", payload);

            const response = await HttpService.post("api/v1/auth/forgot/password", payload);
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.emailSent"));
            // return response; // now returns full AuthResponse
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorKey = errorData?.email || errorData?.password || errorData?.hash || "Forgot Password failed";

            const message = getAuthErrorMessage(errorKey, "Forgot Password failed");
            GlobalService.showToast("error", i18n.t("toast.auth.forgetPasswordFailed"), message);
            return rejectWithValue(errorKey);
        }
    }
);

export const ResetPassword = createAsyncThunk<void, ResetPayload>(
    "auth/ResetPassword",
    async (formData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post("api/v1/auth/reset/password", formData);
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.passwordReset"));
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorKey = errorData?.email || errorData?.password || errorData?.hash || "Reset Password failed";

            const message = getAuthErrorMessage(errorKey, "Reset password failed");
            GlobalService.showToast("error", i18n.t("toast.auth.resetFailed"), message);
            return rejectWithValue(errorKey);
        }
    }
);

export const ConfirmEmail = createAsyncThunk<void, ConfirmEmailPayload>(
    "auth/ConfirmEmail",
    async (formData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post("api/v1/auth/email/confirm", formData);
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.emailConfirmed"));
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorKey = errorData?.email || errorData?.error || errorData?.hash || "Email Confirmed failed";

            const message = getAuthErrorMessage(errorKey, "Email Confirmed failed");
            GlobalService.showToast("error", i18n.t("toast.auth.emailConfirmFailed"), message);
            return rejectWithValue(errorKey);
        }
    }
);
export const ResendEmail = createAsyncThunk<void, ResendEmailPayload>(
    "auth/ResendEmail",
    async (formData, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post("api/v1/auth/email/resend", formData);
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.resendEmailSuccess"));
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorKey = errorData?.email || errorData?.error || errorData?.hash || "Resend Email failed";

            const message = getAuthErrorMessage(errorKey, "Resend Email failed");
            GlobalService.showToast("error", i18n.t("toast.auth.resendEmailFailed"), message);
            return rejectWithValue(errorKey);
        }
    }
);
export const getAllJobs = createAsyncThunk<any, { page?: number; limit?: number; filters?: any; search?: string; sort?: any }>(
    "auth/getAllJobs",
    async ({ page = 1, limit = 10, filters = {}, search = "", sort = [{ orderBy: "createdAt", order: "DESC" }] }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            // Build query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            if (search) {
                params.append("search", search);
            }

            if (Object.keys(sort).length > 0) {
                params.append("sort", JSON.stringify(sort));
            }

            if (Object.keys(filters).length > 0) {
                params.append("filters", JSON.stringify(filters));
            }

            const response = await HttpService.get(`api/v1/jobs?${params.toString()}`, true, {});

            dispatch(hideLoader());
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Jobs failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getUserJobs = createAsyncThunk<any, { userId: string; page?: number; limit?: number; filters?: any; search?: string; sort?: any; isShowLoader?: boolean }>(
    "auth/getUserJobs",
    async ({ userId, page = 1, limit = 10, filters = {}, search = "", sort = {}, isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            // Build query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            if (search) {
                params.append("search", search);
            }

            if (Object.keys(filters).length > 0) {
                params.append("filters", JSON.stringify(filters));
            }

            if (Object.keys(sort).length > 0) {
                params.append("sort", JSON.stringify(sort));
            }

            const response = await HttpService.get(`api/v1/jobs/user/${userId}?${params.toString()}`, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting User Jobs failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getJobById = createAsyncThunk<any, { jobId: string; isShowLoader?: boolean }>(
    "auth/getJobById",
    async ({ jobId, isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            const response = await HttpService.get(`api/v1/jobs/${jobId}`, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Job failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const LogoutUser = createAsyncThunk<void, any>(
    "auth/LogoutUser",
    async (data, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response = await HttpService.post(
                "api/v1/auth/logout",
                {},
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.logoutSuccess"));
            
            // Navigate to home page after successful logout
            // Use requestAnimationFrame to ensure navigation happens outside React's render cycle
            requestAnimationFrame(() => {
                // Use replace instead of href to avoid adding to browser history
                window.location.replace("/");
            });
            
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            if (error.data.statusCode == 401) {
                // For 401 errors, manually logout and navigate
                dispatch(LogoutManually());
                requestAnimationFrame(() => {
                    // Use replace instead of href to avoid adding to browser history
                    window.location.replace("/");
                });
                return error.data.statusCode;
            }
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Logout failed";
            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const refreshToken = createAsyncThunk<any, any>(
    "auth/refreshToken",
    async (data, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response = await HttpService.post(
                "api/v1/auth/refresh",
                {},
                { "Content-Type": "application/json" },
                true,
                true
            );
            await GlobalService.setStorage("refreshToken", response.refreshToken);
            await GlobalService.setStorage("token", response.token);
            
            // Reconnect WebSocket with new token
            try {
                const userData = await GlobalService.getStorage("user");
                if (userData && userData.id && response.token) {
                    console.log("🔄 Token refreshed via Redux action, triggering WebSocket reconnection...");
                    await webSocketService.reconnectWithNewToken(response.token, userData.id);
                }
            } catch (wsError) {
                console.warn("⚠️ WebSocket reconnection failed after token refresh:", wsError);
                // Don't fail the token refresh if WebSocket reconnection fails
            }
            
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.tokenRefreshSuccess"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Token Refresh failed";

            GlobalService.showToast("error", i18n.t("toast.auth.tokenRefreshFailed"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);
export const UpdateUserProfile = createAsyncThunk<any, { id: string; data: any }>(
    "auth/UpdateUserProfile",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response = await HttpService.patch(
                `api/v1/users/${id}`,
                data,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.profileUpdated"));

            return response; // This should be the updated user object
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorKey =
                errorData?.email ||
                errorData?.photo ||
                errorData?.role ||
                errorData?.status ||
                "User Update Profile failed";

            const message = getAuthErrorMessage(errorKey, "User Update Profile failed");
            GlobalService.showToast("error", i18n.t("toast.auth.profileUpdateFailed"), message);
            return rejectWithValue(errorKey);
        }
    }
);

export const sendPhoneVerificationCode = createAsyncThunk<any, { phone: string; id: string }>(
    "auth/sendPhoneVerificationCode",
    async ({ phone, id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response = await HttpService.post(
                "api/v1/auth/phone/send-sms",
                { phone, id },
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.verificationCodeSent"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.phone || errorData?.id || "Failed to send verification code";

            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const verifyPhoneOTP = createAsyncThunk<any, { otp: string; id: string }>(
    "auth/verifyPhoneOTP",
    async ({ otp, id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response = await HttpService.post(
                "api/v1/auth/phone/confirm-otp",
                { otp, id },
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.phoneVerified"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.otp || errorData?.id || "Failed to verify OTP";

            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const uploadUserImage = createAsyncThunk<any, { file: any; shouldShowLoader?: boolean }>(
    "auth/uploadUserImage",
    async ({ file, shouldShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (shouldShowLoader) {
                dispatch(showLoader());
            }

            const formData = new FormData();
            formData.append("file", file);

            const response = await HttpService.post(
                "api/v1/files/upload",
                formData,
                { "Content-Type": "multipart/form-data" },
                true
            );

            if (shouldShowLoader) {
                dispatch(hideLoader());
            }

            return response;
        } catch (error: any) {
            if (shouldShowLoader) {
                dispatch(hideLoader());
            }

            const errorData = error?.data?.errors;
            const errorKey =
                errorData?.email ||
                errorData?.photo ||
                errorData?.role ||
                errorData?.status ||
                "User Update Image failed";

            const message = getAuthErrorMessage(errorKey, "User Update Image failed");
            GlobalService.showToast("error", i18n.t("toast.auth.imageUpdateFailed"), message);
            return rejectWithValue(errorKey);
        }
    }
);

export const getAllCurrencies = createAsyncThunk<any, {
    page?: number;
    limit?: number;
    search?: string;
    isShowLoader?: boolean;
    append?: boolean;
}>(
    "auth/getAllCurrencies",
    async ({ page = 1, limit = 10, search = "", isShowLoader = true, append = false }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Build query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            if (search && search.trim()) {
                params.append("name", search.trim());
            }

            const response = await HttpService.get(`api/v1/currencies?${params.toString()}`, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return {
                    data: [],
                    hasNextPage: false,
                    totalCount: 0,
                };
            } else {
                return {
                    data: response.data || response,
                    totalCount: response.totalCount || 0,
                    hasNextPage: response.hasNextPage || false,
                };
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Currencies failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// Get currency by ID
export const getCurrencyById = createAsyncThunk<any, {
    id: string;
    isShowLoader?: boolean;
}>(
    "auth/getCurrencyById",
    async ({ id, isShowLoader = false }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            const response = await HttpService.get(`api/v1/currencies/${id}`, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            return response;
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Currency failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getAllTimezones = createAsyncThunk<any, {
    page?: number;
    limit?: number;
    search?: string;
    isShowLoader?: boolean;
}>(
    "auth/getAllTimezones",
    async ({ page = 1, limit = 10, search = "", isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Build query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            if (search && search.trim()) {
                params.append("name", search.trim());
            }

            const response = await HttpService.get(`api/v1/timezones?${params.toString()}`, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return {
                    data: [],
                    hasNextPage: false,
                    totalCount: 0,
                };
            } else {
                return {
                    data: response.data || response,
                    totalCount: response.totalCount || 0,
                    hasNextPage: response.hasNextPage || false,
                };
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Timezones failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const detectUserLocation = createAsyncThunk<any, {
    ipAddress?: string;
    isShowLoader?: boolean;
}>(
    "auth/detectUserLocation",
    async ({ ipAddress, isShowLoader = false }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // If no IP provided, try to get client's public IP first
            let clientIP = ipAddress;
            if (!clientIP) {
                console.log("📡 No IP provided, detecting client public IP...");
                try {
                    // Use ipify to get the client's real public IP (works with VPN)
                    const ipResponse = await fetch("https://api.ipify.org?format=json");
                    const ipData = await ipResponse.json();
                    clientIP = ipData.ip;
                    console.log(`✅ Detected client public IP: ${clientIP}`);
                } catch (ipError) {
                    console.warn("⚠️ Failed to detect client IP, backend will use request IP:", ipError);
                }
            } else {
                console.log(`📡 Using provided IP: ${clientIP}`);
            }

            const response = await HttpService.post(
                "api/v1/auth/detect-location",
                { ipAddress: clientIP },
                {},
                false // No auth required
            );

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            return response;
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }

            return rejectWithValue(error?.data?.message || "Location detection failed");
        }
    }
);

export const getAllLanguages = createAsyncThunk<any, {
    page?: number;
    limit?: number;
    search?: string;
    isShowLoader?: boolean;
}>(
    "auth/getAllLanguages",
    async ({ page = 1, limit = 40, search = "", isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            // Build query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());

            if (search && search.trim()) {
                params.append("name", search.trim());
            }

            const response = await HttpService.get(`api/v1/languages?${params.toString()}`, true, {});

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            if (typeof response === "string" && response.length === 0) {
                return {
                    data: [],
                    hasNextPage: false,
                    totalCount: 0,
                };
            } else {
                return {
                    data: response.data || response,
                    totalCount: response.totalCount || 0,
                    hasNextPage: response.hasNextPage || false,
                };
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Getting Languages failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const createGeneralClientSetting = createAsyncThunk<any, { data: any }>(
    "auth/createGeneralClientSetting",
    async ({ data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response = await HttpService.post(
                "api/v1/general-settings",
                data,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            // GlobalService.showToast("success", "Success", "General Settings Created Successfully");
            return response;
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "General Settings created failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const UpdateClientGeneralSettings = createAsyncThunk<any, { id: string; data: any }>(
    "auth/UpdateClientGeneralSettings",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.patch(
                `api/v1/general-settings/${id}`,
                data,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            // GlobalService.showToast("success", "Success", "General Settings Updated Successfully");
            return response;
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "General Settings Update failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const getUserGeneralSettingAll = createAsyncThunk<any, any>(
    "auth/getUserGeneralSettingAll",
    async ({ id, isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            const response = await HttpService.get(
                `api/v1/general-settings/user/all/${id}`,

                true,
                {}
            );
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            if (typeof response == "string" && response.length == 0) {
                return null;
            } else {
                return response;
            }
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "General Settings failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const deleteUserGeneralSetting = createAsyncThunk<any, { id: string }>(
    "professionals/deleteUserGeneralSetting",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.deleteRecord(
                `api/v1/general-settings/${id}`,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.general.languageDeleted"));
            return { id };
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User deleted language failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const getUserGeneralSetting = createAsyncThunk<any, any>(
    "auth/getUserGeneralSetting",
    async ({ id, isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            const response = await HttpService.get(
                `api/v1/general-settings/user/${id}`,

                true,
                {}
            );
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            if (typeof response == "string" && response.length == 0) {
                return null;
            } else {
                return response;
            }
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "General Settings failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);


export const getUserTickets = createAsyncThunk<any, { id: string; isShowLoader?: boolean }>(
    "auth/getUserTickets",
    async ({ id, isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            const response = await HttpService.get(
                `api/v1/support-tickets/user/${id}`,

                true,
                {}
            );
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            if (typeof response === "string" && response.length === 0) {
                return [];
            } else {
                return response;
            }

            // GlobalService.showToast("success", "Success", "User Preferences Updated Successfully");
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Getting Tickets failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const getAllTickets = createAsyncThunk<any, { 
    page: number; 
    limit: number; 
    category?: string | number;
    priority?: string | number;
    status?: string | number;
    search?: string;
    sortBy?: string;
    sortOrder?: string;
    isShowLoader?: boolean;
}>(
    "auth/getAllTickets",
    async ({ 
        page, 
        limit, 
        category,
        priority,
        status,
        search,
        sortBy = "createdAt",
        sortOrder = "desc",
        isShowLoader = true 
    }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            
            // Build query parameters
            const params = new URLSearchParams();
            params.append("page", page.toString());
            params.append("limit", limit.toString());
            
            // Add filter parameters if provided
            if (category && category !== "all") {
                params.append("category", category.toString());
            }
            
            if (priority && priority !== "all") {
                params.append("priority", priority.toString());
            }
            
            if (status && status !== "all") {
                params.append("status", status.toString());
            }
            
            if (search && search.trim()) {
                params.append("search", search.trim());
            }
            
            // Add sorting parameters
            if (sortBy && sortOrder) {
                params.append("sortBy", sortBy);
                params.append("sortOrder", sortOrder.toUpperCase());
            }
            
            const response = await HttpService.get(
                `api/v1/support-tickets?${params.toString()}`,
                true,
                {}
            );
            
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            
            console.log("getAllTickets response:", response);
            console.log("Applied filters:", { category, priority, status, search, sortBy, sortOrder });

            if (typeof response === "string" && response.length === 0) {
                return {
                    data: [],
                    hasNextPage: false,
                    totalCount: 0,
                };
            } else {
                return {
                    data: response.data,
                    totalCount: response.totalCount || 0,
                    hasNextPage: response.hasNextPage || false,
                };
            }
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "All Getting Tickets failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const updateUserTickets = createAsyncThunk<any, { id: string; data: any }>(
    "auth/updateUserTickets",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response = await HttpService.patch(
                `api/v1/support-tickets/${id}`,
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.tickets.updated"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Update Tickets failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const createUserTickets = createAsyncThunk<any, { data: any }>(
    "auth/createUserTickets",
    async ({ data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post(
                "api/v1/support-tickets",
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.tickets.created"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User created tickets failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const deleteUserTicket = createAsyncThunk<any, { id: string }>(
    "auth/deleteUserTicket",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.deleteRecord(
                `api/v1/support-tickets/${id}`,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.tickets.deleted"));
            return { id };
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User deleted tickets failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const UpdateUserPassword = createAsyncThunk<User, { data: any }>(
    "auth/UpdateUserPassword",
    async ({ data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());
            const response = await HttpService.patch(
                "api/v1/auth/me",
                data,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());
            console.log(response);

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.auth.passwordUpdated"));

            return response; // This should be the updated user object
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorKey =
                errorData?.email ||
                errorData?.password ||
                errorData?.oldPassword ||
                errorData?.photo ||
                errorData?.role ||
                errorData?.status ||
                "User Update Password failed";

            const message = getAuthErrorMessage(errorKey, "User Update Password failed");
            GlobalService.showToast("error", i18n.t("toast.auth.passwordUpdateFailed"), message);
            return rejectWithValue(errorKey);
        }
    }
);
export const createChat = createAsyncThunk<any, { data: any; isShowLoader?: boolean }>(
    "auth/createChat",
    async ({ data, isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            const response = await HttpService.post("api/v1/chats", data, { "Content-Type": "application/json" }, true);
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            // GlobalService.showToast("success", "Success", "User chats Created Successfully");
            return response;
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Chats created failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getUserChats = createAsyncThunk<any, any>(
    "auth/getUserChats",
    async ({ id: contextId }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.get(
                `api/v1/chats/context/${contextId}`,

                true,
                {}
            );
            dispatch(hideLoader());
            if (typeof response == "string" && response.length == 0) {
                return null;
            } else {
                return response;
            }
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Chats get  failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const UpdateUserChats = createAsyncThunk<any, { id: string; data: any }>(
    "auth/UpdateUserChats",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.patch(
                `api/v1/chats/${id}`,
                data,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());
            // GlobalService.showToast("success", "Success", "User chats Updated Successfully");
            return response;
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Update chats failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const deleteUserChat = createAsyncThunk<any, { id: string }>(
    "auth/deleteUserChat",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.deleteRecord(
                `api/v1/chats/${id}`,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.chats.deleted"));
            return { id };
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User deleted chats failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const createConversation = createAsyncThunk<any, { data: any; isShowLoader?: boolean }>(
    "auth/createConversation",
    async ({ data, isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }
            const response = await HttpService.post(
                "api/v1/conversations",
                data,
                { "Content-Type": "application/json" },
                true
            );
            if (isShowLoader) {
                dispatch(hideLoader());
            }
            console.log(response, "conversation response");

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.conversations.created"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User conversations created failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getUserConversations = createAsyncThunk<any, any>(
    "auth/getUserConversations",
    async ({ id: chatId }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.get(
                `api/v1/conversations/chat/${chatId}`,

                true,
                {}
            );
            dispatch(hideLoader());
            return response;
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Conversations get  failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const UpdateUserConversation = createAsyncThunk<any, { id: string; data: any }>(
    "auth/UpdateUserConversation",
    async ({ id, data }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.patch(
                `api/v1/conversations/${id}`,
                data,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.conversations.updated"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User Update conversations failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);
export const deleteUserConversation = createAsyncThunk<any, { id: string }>(
    "auth/deleteUserConversation",
    async ({ id }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.deleteRecord(
                `api/v1/conversations/${id}`,
                { "Content-Type": "application/json" },
                true
            );
            dispatch(hideLoader());

            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.conversations.deleted"));
            return { id };
        } catch (error: any) {
            dispatch(hideLoader());

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "User deleted conversations failed";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const getUserById = createAsyncThunk<any, { id: string; isShowLoader?: boolean }>(
    "auth/getUserById",
    async ({ id, isShowLoader = true }, { dispatch, rejectWithValue }) => {
        try {
            if (isShowLoader) {
                dispatch(showLoader());
            }

            const response = await HttpService.get(
                `api/v1/users/${id}`,
                true,
                {}
            );

            if (isShowLoader) {
                dispatch(hideLoader());
            }

            return response;
        } catch (error: any) {
            if (isShowLoader) {
                dispatch(hideLoader());
            }

            const errorData = error?.data?.errors;
            const errorMessage = errorData?.message || "Failed to fetch user data";

            console.log(error);
            return rejectWithValue(errorMessage);
        }
    }
);

// Save user category selections
export const saveUserCategorySelections = createAsyncThunk<any, {
    userId: string;
    categories: { id: number }[];
    subcategories: { id: number }[]
}>(
    "auth/saveUserCategorySelections",
    async ({ userId, categories, subcategories }, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            // TODO: Replace with actual API endpoint when available
            // For now, we'll save this data as part of the user profile
            const response = await HttpService.patch(
                `api/v1/users/${userId}`,
                {
                    categories: categories,
                    subcategories: subcategories
                },
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
                GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.categories.selectionsSaved"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.hash || errorData?.email || "Failed to save category selections";
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);

export const assignProfessionalWhatsApp = createAsyncThunk<any, {
    to: string;
    var1_name: string;
    var2_jobTitle: string;
    var3_jobDescription: string;
    var4_clientName: string;
    var5_clientPhone: string;
    var6_clientWhatsapp: string;
    var7_clientEmail: string;
    var8_clientAddress: string;
    var9_detailsUrl: string;
}>(
    "auth/assignProfessionalWhatsApp",
    async (data, { dispatch, rejectWithValue }) => {
        try {
            dispatch(showLoader());

            const response = await HttpService.post(
                "api/v1/auth/whatsapp/assign-professional",
                data,
                { "Content-Type": "application/json" },
                true
            );

            dispatch(hideLoader());
            GlobalService.showToast("success", i18n.t("toast.titles.success"), i18n.t("toast.whatsapp.messageSent"));
            return response;
        } catch (error: any) {
            dispatch(hideLoader());
            const errorData = error?.data?.errors;
            const errorMessage = errorData?.to || errorData?.message || "Failed to send WhatsApp message to professional";
            GlobalService.showToast("error", i18n.t("toast.titles.error"), errorMessage);
            return rejectWithValue(errorMessage);
        }
    }
);
